import { Button, Card, Col, Form, Input, InputNumber, Layout, Row, Switch, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';

const { Content } = Layout;
const { Title, Text } = Typography;

const SystemSettings = () => {

    const { currentEventId, setCurrentEventId } = useAuth();
    const [form] = Form.useForm();
    const [exchangeRateForm] = Form.useForm();
    const [eligibilityForm] = Form.useForm();
    const [registrationForm] = Form.useForm();
    const [schedule, setSchedule] = useState([]);
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
    const [registrationLoading, setRegistrationLoading] = useState(false);

    useEffect(() => {
        form.setFieldsValue({ currentEventId });
        fetchSettings();
    }, [currentEventId, form]);

    const fetchSettings = async () => {
        try {
            const res = await apis.systemSettings.getGlobalSettings();
            if (res.data?.ticketEligibility) {
                const { enabled, schedule: s } = res.data.ticketEligibility;
                setIsEnabled(enabled || false);
                setSchedule(s || []);
            }
            if (res.data?.usdToIdrRate) {
                exchangeRateForm.setFieldsValue({ usdToIdrRate: res.data.usdToIdrRate });
            } else {
                exchangeRateForm.setFieldsValue({ usdToIdrRate: 17800 });
            }
            if (res.data?.isRegistrationEnabled !== undefined) {
                registrationForm.setFieldsValue({ isRegistrationEnabled: res.data.isRegistrationEnabled });
            } else {
                registrationForm.setFieldsValue({ isRegistrationEnabled: true });
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
        }
    };

    const onFinish = async (values) => {
        try {
            const newEventId = values.currentEventId.trim();
            if (!newEventId) {
                message.error('Event ID cannot be empty');
                return;
            }

            const res = await apis.systemSettings.updateGlobalSettings({ currentEventId: newEventId });
            if (res.error) throw new Error(res.error);

            setCurrentEventId(newEventId);
            message.success(`Successfully updated global event ID to ${newEventId}`);
        } catch (error) {
            console.error("Error updating global settings:", error);
            message.error("Failed to update global event ID.");
        }
    };

    const handleSaveEligibility = async (newIsEnabled = isEnabled, newSchedule = schedule) => {
        setLoading(true);
        try {
            const payload = {
                ticketEligibility: {
                    enabled: newIsEnabled,
                    schedule: newSchedule
                }
            };
            const res = await apis.systemSettings.updateGlobalSettings(payload);
            if (res.error) throw new Error(res.error);

            setIsEnabled(newIsEnabled);
            setSchedule(newSchedule);
            message.success('Ticket eligibility settings updated');
        } catch (error) {
            console.error("Error updating eligibility:", error);
            message.error("Failed to update ticket eligibility settings.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveExchangeRate = async (values) => {
        setExchangeRateLoading(true);
        try {
            const res = await apis.systemSettings.updateGlobalSettings({ usdToIdrRate: values.usdToIdrRate });
            if (res.error) throw new Error(res.error);
            message.success(`Exchange rate updated to Rp ${values.usdToIdrRate.toLocaleString('id-ID')} per USD`);
        } catch (error) {
            console.error("Error updating exchange rate:", error);
            message.error("Failed to update exchange rate.");
        } finally {
            setExchangeRateLoading(false);
        }
    };

    const handleSaveRegistrationStatus = async (values) => {
        setRegistrationLoading(true);
        try {
            const res = await apis.systemSettings.updateGlobalSettings({ isRegistrationEnabled: values.isRegistrationEnabled });
            if (res.error) throw new Error(res.error);
            message.success(`Registration is now ${values.isRegistrationEnabled ? 'enabled' : 'disabled'}`);
        } catch (error) {
            console.error("Error updating registration status:", error);
            message.error("Failed to update registration status.");
        } finally {
            setRegistrationLoading(false);
        }
    };

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <Title level={2}>System Settings</Title>
            <Text type="secondary">Manage global application configurations here. Changes apply immediately across the entire platform.</Text>

            <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                <Col span={12}>
                    <Card title="Event Configuration" bordered={false} style={{ height: '100%' }}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                        >
                            <Form.Item
                                label="Current Event ID"
                                name="currentEventId"
                                rules={[{ required: true, message: 'Please input the active event ID!' }]}
                                extra="This determines which event registrants will be added to, and which seat collections will be queried."
                            >
                                <Input placeholder="e.g. APCS2026" />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit">
                                    Save Changes
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Exchange Rate Configuration" bordered={false} style={{ height: '100%' }}>
                        <Form
                            form={exchangeRateForm}
                            layout="vertical"
                            onFinish={handleSaveExchangeRate}
                        >
                            <Form.Item
                                label="USD to IDR Rate"
                                name="usdToIdrRate"
                                rules={[
                                    { required: true, message: 'Please input the exchange rate!' },
                                    { type: 'number', min: 10000, max: 25000, message: 'Rate must be between 10,000 and 25,000 IDR' }
                                ]}
                                extra="This rate is used for converting USD prices to IDR on Paper.id invoices. Must be between 10,000 and 25,000."
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={10000}
                                    max={25000}
                                    step={100}
                                    placeholder="e.g. 17800"
                                    formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={value => value.replace(/Rp\s?|\./g, '')}
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={exchangeRateLoading}>
                                    Save Exchange Rate
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Registration Configuration" bordered={false} style={{ height: '100%' }}>
                        <Form
                            form={registrationForm}
                            layout="vertical"
                            onFinish={handleSaveRegistrationStatus}
                        >
                            <Form.Item
                                label="Enable Registration Page"
                                name="isRegistrationEnabled"
                                valuePropName="checked"
                                extra="If disabled, users trying to access the registration page will see an elegant 'Under Maintenance' screen."
                            >
                                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={registrationLoading}>
                                    Save Registration Status
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </Content>
    );
}

export default SystemSettings;
